import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaCheck,
  FaQuoteLeft,
  
  FaEye,
  FaDollarSign,
  FaLock,
  FaBolt,
  FaChartLine,
  FaUsers,
  FaSmile,
  FaHeartbeat,
  FaTrophy,
  FaCogs,
  FaProcedures,
  FaTools,
  FaLeaf,
  FaShoppingCart,
  FaBuilding,
  FaRuler,
  FaStar,

  FaClipboard,
  FaWarehouse,
  FaTruck,
  FaBox,
  FaMinusCircle,
  FaClock,
  FaGraduationCap,
  FaRocket,
  FaGlobe,
  FaQuestionCircle
} from 'react-icons/fa';

import SEOHelmet from '../SEOHelmet';
import Navbar from './Navbar';
import { industriesData } from '../../data/IndustriesData';
import '../../styles/landing-page/IndustryInfoPage.css';

// Icon mapping
const iconMap = {
  FaEye,
  FaDollarSign,
  FaLock,
  FaBolt,
  FaChartLine,
  FaUsers,
  FaSmile,
  FaHeartbeat,
  FaTrophy,
  FaCogs,
  FaProcedures,
  FaTools,
  FaLeaf,
  FaShoppingCart,
  FaBuilding,
  FaRuler,
  FaStar,
  FaClipboard,
  FaWarehouse,
  FaTruck,
  FaBox,
  FaMinusCircle,
  FaClock,
  FaGraduationCap,
  FaRocket,
  FaGlobe,
  FaQuestionCircle
};

const getIcon = (iconName) => {
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent /> : null;
};

const IndustryInfoPage = () => {
  const { industryId } = useParams();
  const [activeSection, setActiveSection] = useState('industry');
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Get industry data based on ID
    const industryData = industriesData[industryId] || industriesData.it;
    setIndustry(industryData);
    setLoading(false);
  }, [industryId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!industry) {
    return <div>Industry not found</div>;
  }

  return (
    <div className="industry-info-page">
      <SEOHelmet
        page="industryInfo"
        replacements={{ industry: industry?.name || industryId || 'industry' }}
        canonicalUrl={`https://www.assetflows.in/industry/${industryId || 'it'}`}
      />
      {/* Navbar */}
      <Navbar activeSection={activeSection} setActiveSection={setActiveSection} />

      {/* Header Section */}
      <header className="industry-header">
        <div className="industry-header__content">
          <h1>{industry.name}</h1>
          <p className="industry-tagline">{industry.tagline}</p>
          <p className="industry-location">📍 {industry.location}</p>
        </div>
        <div className="industry-header__image">
          <img src={industry.heroimg} alt={`${industry.name} Hero`} />
        </div>
      </header>

      {/* Main Content */}
      <main className="industry-main">
        {/* Description Section */}
        <section className="industry-description-section">
          <div className="industry-container">
            <p className="industry-description">{industry.description}</p>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="industry-features-section">
          <div className="industry-container">
            <h2 className="section-title"><FaRocket /> Key Features</h2>
            <div className="features-grid">
              {industry.keyFeatures.map((feature) => (
                <div key={feature.id} className="feature-card">
                  <div className="feature-icon">
                    <FaCheck />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="industry-benefits-section">
          <div className="industry-container">
            <h2 className="section-title"><FaStar /> Benefits</h2>
            <div className="benefits-grid">
              {industry.benefits.map((benefit) => (
                <div key={benefit.id} className="benefit-card">
                  <div className="benefit-icon">{getIcon(benefit.icon)}</div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Client Reviews Section */}
        <section className="industry-reviews-section">
          <div className="industry-container">
            <h2 className="section-title"><FaGlobe /> Overseas Client Reviews</h2>
            <div className="reviews-grid">
              {industry.clientReviews.map((review) => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <h4>{review.country}</h4>
                    <p className="review-type">{review.type}</p>
                  </div>
                  <div className="review-quote">
                    <FaQuoteLeft className="quote-icon" />
                    <p>{review.review}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="industry-faq-section">
          <div className="industry-container">
            <h2 className="section-title"><FaQuestionCircle /> Frequently Asked Questions</h2>
            <div className="faq-accordion">
              {industry.faqs.map((faq) => (
                <div
                  key={faq.id}
                  className={`faq-item ${expandedFAQ === faq.id ? 'active' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                    type="button"
                  >
                    <span>{faq.question}</span>
                    <span className="faq-arrow" />
                  </button>
                  {expandedFAQ === faq.id && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="industry-cta-section">
          <div className="industry-container">
            <h2>Ready to Transform Your Industry Operations?</h2>
            <p>Contact Asset Flow today for a personalized demo and discover how our solution can benefit your organization.</p>
            <button className="cta-button">Schedule a Demo</button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="industry-footer">
        <div className="industry-container">
          <div className="footer-content">
            <p>&copy; 2024 AssetFlows. All rights reserved.</p>
            <p>Transforming Asset Management Across Industries</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IndustryInfoPage;
